import { Injectable, OnDestroy } from '@angular/core';
import moment from 'moment-timezone';
import { DomHandler } from 'primeng/dom';
import { filter, Subscription } from 'rxjs';

import { CalendarViewsEnum, NavigationDirectionsEnum } from '../enums/date-picker.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DateFormatModel } from '../../../Core/Users/models/date-format.model';
import { dateFormats } from '../../../Core/Users/data/date-formats';



@Injectable()
export class DatePickerService implements OnDestroy {
    dateFormatModel: DateFormatModel = dateFormats[1];

    subscriptions: Subscription[] = [];

    constructor(
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.dateFormatModel = dateFormats.find(format => format.id === prefs.dateFormatId);
            })
        );
    }

    getFormatHint(range: boolean = false) {
        if (range) {
            return this.localizationService.transform(this.dateFormatModel.name)
            + ' - ' +
            this.localizationService.transform(this.dateFormatModel.name);
        } else {
            return this.localizationService.transform(this.dateFormatModel.name);
        }
    }

    getWeekDays(): string[] {
        const weekDays: string[] = [];
        for (let i = 0; i < 7; i++) {
            weekDays.push(
                this.localizationService.transform('Common.datePicker.dayNameMin.' + i)
            );
        }

        return weekDays;
    }

    getMonthTitleValues(): string[] {
        const monthTitleValues: string[] = [];
        for (let i = 0; i < 12; i++) {
            monthTitleValues.push(
                this.localizationService.transform('Common.datePicker.monthName.' + i)
            );
        }

        return monthTitleValues;
    }

    getMonthPickerValues(): string[] {
        const monthPickerValues: string[] = [];
        for (let i = 0; i < 12; i++) {
            monthPickerValues.push(
                this.localizationService.transform('Common.datePicker.monthNameShort.' + i)
            );
        }

        return monthPickerValues;
    }

    getYearPickerValues(currentYear: number): string[] {
        const yearPickerValues: string[] = [];
        const base = currentYear - (currentYear % 10);
        for (let i = 0; i < 10; i++) {
            yearPickerValues.push(base + i + '');
        }

        return yearPickerValues;
    }

    getPreviousMonthAndYear(month: number, year: number) {
        let m, y;

        if (month === 0) {
            m = 11;
            y = year - 1;
        } else {
            m = month - 1;
            y = year;
        }

        return { month: m, year: y };
    }

    getNextMonthAndYear(month: number, year: number) {
        let m, y;

        if (month === 11) {
            m = 0;
            y = year + 1;
        } else {
            m = month + 1;
            y = year;
        }

        return { month: m, year: y };
    }

    createMonth(value, month: number, year: number, minDate: Date, maxDate: Date, disabledDates: Date[], disabledDays: number[], selectionMode: string) {
        const dates = [];
        const firstDayIndex = new Date(year, month, 1).getDay();
        const daysLength = this.getDaysCountInMonth(month, year);
        const prevMonthDaysLength = this.getDaysCountInPrevMonth(month, year);
        const today = new Date();
        const monthRows = Math.ceil((daysLength + firstDayIndex) / 7);
        let dayNo = 1;
        let date;

        for (let i = 0; i < monthRows; i++) {
            const week = [];
            if (i === 0) {
                for (let j = prevMonthDaysLength - firstDayIndex + 1; j <= prevMonthDaysLength; j++) {
                    const prev = this.getPreviousMonthAndYear(month, year);
                    date = {
                        day: j,
                        month: prev.month,
                        year: prev.year,
                        otherMonth: true,
                        today: this.isToday(today, j, prev.month, prev.year),
                        selectable: this.isSelectable(j, prev.month, prev.year, minDate, maxDate, disabledDates, disabledDays, true),
                    };
                    date.selected = this.isSelected(value, date, selectionMode);
                    date.ariaLabel = moment(`${prev.year}-${prev.month + 1}-${j}`, 'YYYY-MM-DD').format('dddd, DD MMMM');
                    if (value && selectionMode === 'range') {
                        if (value.length > 1) {
                            date.selectedRangeStart = this.isDateEquals(value[0], date);
                            date.selectedRangeEnd = this.isDateEquals(value[1], date);
                            date.selectedInRange = this.isDateBetween(value[0], value[1], date);
                        } else {
                            date.selectedRangeStart = this.isDateEquals(value[0], date);
                            date.selectedRangeEnd = this.isDateEquals(value[0], date);
                            date.selectedInRange = false;
                        }
                    }
                    week.push(date);
                }

                const remainingDaysLength = 7 - week.length;
                for (let j = 0; j < remainingDaysLength; j++) {
                    date = {
                        day: dayNo,
                        month: month,
                        year: year,
                        today: this.isToday(today, dayNo, month, year),
                        selectable: this.isSelectable(dayNo, month, year, minDate, maxDate, disabledDates, disabledDays, false),
                    };
                    date.selected = this.isSelected(value, date, selectionMode);
                    date.ariaLabel = moment(`${year}-${month + 1}-${dayNo}`, 'YYYY-MM-DD').format('dddd, DD MMMM');
                    if (value && selectionMode === 'range') {
                        if (value.length > 1) {
                            date.selectedRangeStart = this.isDateEquals(value[0], date);
                            date.selectedRangeEnd = this.isDateEquals(value[1], date);
                            date.selectedInRange = this.isDateBetween(value[0], value[1], date);
                        } else {
                            date.selectedRangeStart = this.isDateEquals(value[0], date);
                            date.selectedRangeEnd = this.isDateEquals(value[0], date);
                            date.selectedInRange = false;
                        }
                    }
                    week.push(date);
                    dayNo++;
                }
            } else {
                for (let j = 0; j < 7; j++) {
                    if (dayNo > daysLength) {
                        const next = this.getNextMonthAndYear(month, year);
                        date = {
                            day: dayNo - daysLength,
                            month: next.month,
                            year: next.year,
                            otherMonth: true,
                            today: this.isToday(today, dayNo - daysLength, next.month, next.year),
                            selectable: this.isSelectable(dayNo - daysLength, next.month, next.year, minDate, maxDate, disabledDates, disabledDays, true),
                        };
                    } else {
                        date = {
                            day: dayNo,
                            month: month,
                            year: year,
                            today: this.isToday(today, dayNo, month, year),
                            selectable: this.isSelectable(dayNo, month, year, minDate, maxDate, disabledDates, disabledDays, false),
                        };
                    }
                    date.selected = this.isSelected(value, date, selectionMode);
                    date.ariaLabel = moment(`${date.year}-${date.month + 1}-${date.day}`, 'YYYY-MM-DD').format('dddd, DD MMMM');
                    if (value && selectionMode === 'range') {
                        if (value.length > 1) {
                            date.selectedRangeStart = this.isDateEquals(value[0], date);
                            date.selectedRangeEnd = this.isDateEquals(value[1], date);
                            date.selectedInRange = this.isDateBetween(value[0], value[1], date);
                        } else {
                            date.selectedRangeStart = this.isDateEquals(value[0], date);
                            date.selectedRangeEnd = this.isDateEquals(value[0], date);
                            date.selectedInRange = false;
                        }
                    }
                    week.push(date);
                    dayNo++;
                }
            }
            dates.push(week);
        }

        return {
            month: month,
            year: year,
            dates: dates,
        };
    }

    getDaysCountInPrevMonth(month: number, year: number): number {
        const prev = this.getPreviousMonthAndYear(month, year);
        return this.getDaysCountInMonth(prev.month, prev.year);
    }

    getDaysCountInMonth(month: number, year: number): number {
        return moment(`${year}-${month + 1}`, 'YYYY-MM').daysInMonth();
    }

    parseValueFromString(text: string, selectionMode: string, validDateFormats: string[]) {
        if (!text || text.trim().length === 0) {
            return null;
        }
        let value: any;
        if (selectionMode === 'single') {
            return moment(text, validDateFormats).isValid() ?
                moment(text, validDateFormats).toDate() : null;
        } else {
            const tokens = text.split(' - ');
            value = [];
            for (let i = 0; i < tokens.length; i++) {
                if (!moment(tokens[i].trim(), validDateFormats).isValid()) {
                    return false;
                }
                value[i] = moment(tokens[i].trim(), validDateFormats).toDate();
            }
            return value;
        }
    }

    isSelected(value, dateMeta, selectionMode): boolean {
        if (value) {
            if (selectionMode === 'single') {
                return this.isDateEquals(value, dateMeta);
            } else if (selectionMode === 'range') {
                if (value[1]) {
                    return this.isDateEquals(value[0], dateMeta)||
                        this.isDateEquals(value[1], dateMeta) ||
                        this.isDateBetween(value[0], value[1], dateMeta);
                } else {
                    return this.isDateEquals(value[0], dateMeta);
                }
            }
        } else {
            return false;
        }
    }

    isDateEquals(value, dateMeta): boolean {
        return value && value instanceof Date ?
            value.getDate() === dateMeta.day && value.getMonth() === dateMeta.month && value.getFullYear() === dateMeta.year :
            false;
    }

    isDateBetween(start: Date, end: Date, dateMeta): boolean {
        const between: boolean = false;
        if (start && end) {
            const date: Date = new Date(dateMeta.year, dateMeta.month, dateMeta.day);
            return start.getTime() <= date.getTime() && end.getTime() >= date.getTime();
        }
        return between;
    }

    isToday(today: Date, day: number, month: number, year: number): boolean {
        return today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
    }

    isDateDisabled(day: number, month: number, year: number, disabledDates: Date[]): boolean {
        for (const disabledDate of disabledDates) {
            if (disabledDate.getFullYear() === year && disabledDate.getMonth() === month && disabledDate.getDate() === day) {
                return true;
            }
        }

        return false;
    }

    isDayDisabled(day: number, month: number, year: number, disabledDays): boolean {
        const weekdayNumber = new Date(year, month, day).getDay();
        return disabledDays.indexOf(weekdayNumber) !== -1;
    }

    isSelectable(
        day: number, month: number, year: number, minDate: Date, maxDate: Date, disabledDates: Date[], disabledDays: number[], otherMonth: boolean
    ): boolean {
        let validMin = true;
        let validMax = true;
        let validDate = true;
        let validDay = true;

        if (otherMonth) {
            return false;
        }

        if (minDate) {
            if (minDate.getFullYear() > year) {
                validMin = false;
            } else if (minDate.getFullYear() === year) {
                if (minDate.getMonth() > month) {
                    validMin = false;
                } else if (minDate.getMonth() === month) {
                    if (minDate.getDate() > day) {
                        validMin = false;
                    }
                }
            }
        }
        if (maxDate) {
            if (maxDate.getFullYear() < year) {
                validMax = false;
            } else if (maxDate.getFullYear() === year) {
                if (maxDate.getMonth() < month) {
                    validMax = false;
                } else if (maxDate.getMonth() === month) {
                    if (maxDate.getDate() < day) {
                        validMax = false;
                    }
                }
            }
        }

        if (disabledDates) {
            validDate = !this.isDateDisabled(day, month, year, disabledDates);
        }
        if (disabledDays) {
            validDay = !this.isDayDisabled(day, month, year, disabledDays);
        }

        return validMin && validMax && validDate && validDay;
    }

    isValidSelection(value, minDate: Date, maxDate: Date, disabledDates: Date[], disabledDays: number[], selectionMode: string): boolean {
        let isValid = true;
        if (selectionMode === 'single') {
            if (!this.isSelectable(value.getDate(), value.getMonth(), value.getFullYear(), minDate, maxDate, disabledDates, disabledDays, false)) {
                isValid = false;
            }
        } else if (value.every((v) => this.isSelectable(v.getDate(), v.getMonth(), v.getFullYear(), minDate, maxDate, disabledDates, disabledDays, false))) {
            if (selectionMode === 'range') {
                isValid = value.length > 1 && value[1] > value[0] ? true : false;
            }
        }
        return isValid;
    }

    isFalseClickOutside(element: HTMLElement): boolean {
        return (element && Array.from(element.classList).some(name => name.indexOf('agpr-datePicker') > -1));
    }

    formatDate(date, validDateFormats) {
        return moment(date, validDateFormats).isValid() ?
            moment(date).format(this.dateFormatModel.momentFormat) :
            null;
    }

    initFocus(contentEl: HTMLElement, currentView: CalendarViewsEnum, direction: NavigationDirectionsEnum = null) {
        let cell: HTMLElement;
        switch(currentView) {
            case CalendarViewsEnum.DATE:
                cell = this.initDateCellFocus(contentEl, direction);
                break;
            case CalendarViewsEnum.MONTH:
                cell = this.initMonthCellFocus(contentEl);
                break;
            case CalendarViewsEnum.YEAR:
                cell = this.initYearCellFocus(contentEl);
                break;
        }
        if (cell) {
            cell.setAttribute('tabIndex', '0');
            if (direction) {
                cell.focus();
            }
        }
    }

    initDateCellFocus(contentEl: HTMLElement, direction: NavigationDirectionsEnum): HTMLElement {
        let cell: HTMLElement, cells: HTMLElement[];
        contentEl.querySelectorAll('.agpr-datePicker-dateCell').forEach(dateCell => {
            (dateCell as HTMLElement).setAttribute('tabIndex', '-1');
        });

        if (direction) {
            switch (direction) {
                case NavigationDirectionsEnum.PREV:
                    cells = DomHandler.find(contentEl, '.agpr-datePicker-dateCell:not(.agpr-datePicker-dateCell--disabled)');
                    if (cells && cells.length > 0) {
                        cell = cells[cells.length - 1];
                    }
                    break;
                case NavigationDirectionsEnum.NEXT:
                    cell = DomHandler.findSingle(contentEl, '.agpr-datePicker-dateCell:not(.agpr-datePicker-dateCell--disabled)');
                    break;
            }
        } else {
            cell = contentEl.querySelector('.agpr-datePicker-dateCell--selected');
            if (!cell) {
                cell = contentEl.querySelector('.agpr-datePicker-dateCell--today');
            }
            if (!cell) {
                cell = Array.from(contentEl.querySelectorAll('.agpr-datePicker-dateCell'))
                    .filter(x => !x.classList.contains('agpr-datePicker-dateCell--disabled'))[0] as HTMLElement;
            }
        }

        return cell;
    }

    initMonthCellFocus(contentEl: HTMLElement): HTMLElement {
        let cell: HTMLElement;
        contentEl.querySelectorAll('.agpr-datePicker-monthCell').forEach(monthCell => {
            (monthCell as HTMLElement).setAttribute('tabIndex', '-1');
        });

        cell = contentEl.querySelector('.agpr-datePicker-monthCell--selected');
        if (!cell) {
            cell = Array.from(contentEl.querySelectorAll('.agpr-datePicker-monthCell'))[0] as HTMLElement;
        }

        return cell;
    }

    initYearCellFocus(contentEl: HTMLElement): HTMLElement {
        let cell: HTMLElement;
        contentEl.querySelectorAll('.agpr-datePicker-yearCell').forEach(yearCell => {
            (yearCell as HTMLElement).setAttribute('tabIndex', '-1');
        });

        cell = contentEl.querySelector('.agpr-datePicker-yearCell--selected');
        if (!cell) {
            cell = Array.from(contentEl.querySelectorAll('.agpr-datePicker-yearCell'))[0] as HTMLElement;
        }

        return cell;
    }

    trapFocus(event, contentView: HTMLElement) {
        const focusableElements = DomHandler.getFocusableElements(contentView);

        if (focusableElements?.length > 0) {
            if (!focusableElements[0].ownerDocument.activeElement) {
                focusableElements[0].focus();
            } else {
                const focusedIndex = focusableElements.indexOf(focusableElements[0].ownerDocument.activeElement);

                if (event.shiftKey) {
                    if (focusedIndex === -1 || focusedIndex === 0) {
                        focusableElements[focusableElements.length - 1].focus();
                    } else {
                        focusableElements[focusedIndex - 1].focus();
                    }
                } else {
                    if (focusedIndex === -1 || focusedIndex === focusableElements.length - 1) {
                        focusableElements[0].focus();
                    } else {
                        focusableElements[focusedIndex + 1].focus();
                    }
                }
            }
        }

        event.preventDefault();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
